// API Configuration
import { API_CONFIG } from '../constants/appConstants';

const API_URL = process.env.REACT_APP_API_URL || API_CONFIG.DEFAULT_URL;
const PRODUCTION_URL = API_CONFIG.PRODUCTION_URL;

// Get dealership ID from localStorage
const getDealershipId = () => {
  try {
    // Try 'dealership' first (new format)
    const dealership = localStorage.getItem('dealership');
    if (dealership) {
      const parsed = JSON.parse(dealership);
      return parsed.id || null;
    }
    // Fallback to 'dealer' (current format)
    const dealer = localStorage.getItem('dealer');
    if (dealer) {
      const parsed = JSON.parse(dealer);
      return parsed.id || null;
    }
    // Fallback to old format
    const vincentrixDealer = localStorage.getItem('vincentrix_dealer');
    if (vincentrixDealer) {
      const parsed = JSON.parse(vincentrixDealer);
      return parsed.id || null;
    }
  } catch (error) {
    console.error('Error parsing dealership from localStorage:', error);
  }
  return null;
};

// Get authentication headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('vincentrix_token');
  const dealerId = localStorage.getItem('vincentrix_dealer') 
    ? JSON.parse(localStorage.getItem('vincentrix_dealer')).id 
    : null;
  
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (dealerId) {
    headers['X-Dealer-Id'] = dealerId;
  }
  
  return headers;
};

/**
 * Customer API endpoints
 */
export const customerAPI = {
  /**
   * Get all customers
   * @param {string} store - Store identifier (optional)
   * @returns {Promise<Object>} Response with customers array
   */
  getAll: async (store) => {
    try {
      const url = store ? `${API_URL}/api/customers?store=${store}` : `${API_URL}/api/customers`;
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to fetch customers: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      return response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Get customer by ID
   * @param {string} id - Customer ID
   * @returns {Promise<Object>} Response with customer object
   */
  getById: async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to fetch customer: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      return response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Create a new customer
   * @param {Object} customerData - Customer data object
   * @returns {Promise<Object>} Response with created customer
   */
  create: async (customerData) => {
    const dealershipId = getDealershipId();
    const response = await fetch(`${API_URL}/api/customers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...customerData,
        dealershipId: dealershipId || 'champion-nissan'
      })
    });
    if (!response.ok) {
      let errorMessage = `Failed to create customer: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.errors) {
          const validationErrors = Object.values(errorData.errors).flat();
          errorMessage = validationErrors.join(', ');
        }
      } catch (parseError) {
        // Could not parse error response
      }
      throw new Error(errorMessage);
    }
    return response.json();
  },

  /**
   * Update customer
   * @param {string} id - Customer ID
   * @param {Object} customerData - Updated customer data
   * @returns {Promise<Object>} Response with updated customer
   */
  update: async (id, customerData) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(customerData)
      });
      if (!response.ok) {
        let errorMessage = `Failed to update customer: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          // Could not parse error response
        }
        throw new Error(errorMessage);
      }
      return response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Delete customer
   * @param {string} id - Customer ID
   * @returns {Promise<Object>} Response object
   */
  delete: async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to delete customer: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      return response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Clear all customers
   * @returns {Promise<Object>} Response object
   */
  clearAll: async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/clear-all`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to clear customers: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      return response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Bulk import customers
   * @param {Array} customers - Array of customer objects
   * @returns {Promise<Object>} Response with import results
   */
  bulkImport: async (customers) => {
    const dealershipId = getDealershipId();
    const response = await fetch(`${API_URL}/api/customers/bulk`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ 
        customers,
        dealershipId: dealershipId || 'champion-nissan'
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(`Import failed: ${errorMessage}`);
    }
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Unknown error occurred during import');
    }
    return data;
  },

  /**
   * Add trade-in to customer
   * @param {string} id - Customer ID
   * @param {Object} tradeInData - Trade-in data
   * @returns {Promise<Object>} Response object
   */
  addTradeIn: async (id, tradeInData) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${id}/trade-in`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(tradeInData)
      });
      if (!response.ok) {
        let errorMessage = `Failed to add trade-in: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) errorMessage = errorData.message;
        } catch (parseError) {
          // Could not parse error response
        }
        throw new Error(errorMessage);
      }
      return response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Delete trade-in from customer
   * @param {string} id - Customer ID
   * @param {number} tradeInIndex - Trade-in index
   * @returns {Promise<Object>} Response object
   */
  deleteTradeIn: async (id, tradeInIndex) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${id}/trade-in/${tradeInIndex}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to delete trade-in: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      return response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Get customer conversations
   * @param {string} id - Customer ID
   * @param {AbortSignal} signal - Optional AbortSignal for request cancellation
   * @returns {Promise<Object>} Response with conversations array
   */
  getConversations: async (id, signal) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${id}/conversations`, {
        headers: getAuthHeaders(),
        signal
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to fetch conversations: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      return response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error; // Re-throw abort errors so they can be handled specifically
      }
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw error;
    }
  }
};

/**
 * Analytics API endpoints (Production)
 */
export const analyticsAPI = {
  /**
   * Get analytics data
   * @returns {Promise<Object>} Analytics data
   */
  getAnalytics: async () => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/analytics`);
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to fetch analytics: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch analytics');
      }
      return data;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to analytics server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Get recent actions
   * @returns {Promise<Object>} Recent actions data
   */
  getRecentActions: async () => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/recent-actions`);
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to fetch recent actions: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch recent actions');
      }
      return data;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to analytics server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Get hot leads/opportunities
   * @returns {Promise<Object>} Hot leads data
   */
  getHotLeads: async () => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/hot-leads`);
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to fetch hot leads: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch hot leads');
      }
      return data;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to analytics server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Get appointments
   * @returns {Promise<Object>} Appointments data
   */
  getAppointments: async () => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/appointments`);
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to fetch appointments: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch appointments');
      }
      return data;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to analytics server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Get trade-ins
   * @returns {Promise<Object>} Trade-ins data
   */
  getTradeIns: async () => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/trade-ins`);
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to fetch trade-ins: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch trade-ins');
      }
      return data;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to analytics server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Export customers to CSV
   * @returns {Promise<Blob>} CSV file blob
   */
  exportCustomers: async () => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/export-customers`);
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to export customers: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }
      return response.blob();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to analytics server. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Send test customer reply
   * @param {string} customerId - Customer ID
   * @param {string} message - Message to send
   * @returns {Promise<Object>} Response with AI reply
   */
  sendTestReply: async (customerId, message) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/test-customer-reply/${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message })
      });
      if (!response.ok) {
        let errorMessage = `Failed to send test reply: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) errorMessage = errorData.message;
        } catch (parseError) {
          // Could not parse error response
        }
        throw new Error(errorMessage);
      }
      return response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to analytics server. Please check your connection.');
      }
      throw error;
    }
  }
};

